<template>
  <div>
    <SegmentTimePairTableDialog
      v-if="isMobile()"
      :value="segmentTimePairViewDialog"
      :headers="segmentTimeDataTableHeaders"
      :items="viewingSegmentTimePairs"
      @close="closeViewSegmentTimePairs"
    >
      <template v-slot:startDate="{ item }">
        <v-badge
            :value="!isSegmentStartDateEqual(getCorrectionItemIndex(viewingSegmentTimePairsShift), item)"
            color="primary"
            inline
            text
            dot
            left
        >
          <StartDateTableCell :item="item"/>
        </v-badge>
      </template>
      <template v-slot:clockIn="{ item }">
        <v-badge
          :value="!isSegmentClockInEqual(getCorrectionItemIndex(viewingSegmentTimePairsShift), item)"
          color="primary"
          inline
          text
          dot
          left
        >
          <StartTimeTableCell :item="item"/>
        </v-badge>
      </template>
      <template v-slot:clockOut="{ item }">
        <v-badge
          :value="!isSegmentClockOutEqual(getCorrectionItemIndex(viewingSegmentTimePairsShift), item)"
          color="primary"
          inline
          text
          dot
          left
        >
          <EndTimeTableCell :item="item"/>
        </v-badge>
      </template>
    </SegmentTimePairTableDialog>

    <v-dialog
      :value="value"
      :fullscreen="isMobile()"
      :max-width="!isMenuDrawerClosable() ? '75%' : '92%'"
      scrollable
      @click:outside="closeOperation"
      @keydown.esc="closeOperation"
    >
      <v-card elevation="1">
        <v-card-title>
          {{ $t('titles.viewTimeHistory') }}
          <v-spacer/>
          <v-btn icon @click="closeOperation">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>

        <v-card-text class="pa-0">
          <v-container class="mx-0 px-0" fluid>
            <v-data-table
              :headers="displayedHistoryTableHeaders"
              :items="correctionHistory"
              :loading="loading"
              :mobile-breakpoint="$vuetify.breakpoint.mobileBreakpoint"
              :show-expand="!isMobile()"
              hide-default-footer
              disable-pagination
              disable-sort
              id="correctionHistoryDialog"
              item-key="timePairSummary.id"
              :no-data-text="`${$t('manager.noCorrectionData')}`"
            >
              <template v-slot:item.data-table-expand="{ expand, item, isExpanded }">
                <v-tooltip right :disabled="isMobile()">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      :disabled="item.timePairSummary.segmentTimePairs.length === 0"
                      @click="expand(!isExpanded)"
                    >
                      <v-icon>
                        {{ isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isExpanded ? 'Hide' : 'Show' }} Additional Time Data</span>
                </v-tooltip>
              </template>
              <template v-slot:item.correctionDate="{ item }">
                <v-chip
                  v-if="!item.correctionDate || getCorrectionItemIndex(item) === 0"
                  :color="!item.correctionDate ? 'primary' : 'success'"
                  label
                  text-color="white"
                >
                  <v-icon left>label</v-icon>
                  {{ !item.correctionDate ? $t('tableHeaders.initial') : $t('tableHeaders.current') }}
                </v-chip>
                <div v-else>{{ convertDateToText(item.correctionDate) }}</div>
              </template>
              <template v-slot:item.info="{ item }">
                <TimePairStatusIndicator :items="getRowIndicators(item.timePairSummary)"/>
              </template>
              <template v-slot:item.modifiedBy="{ item }">
                <v-badge
                  :value="item.timePairSummary.modifiedBy.id === employeeId"
                  :left="isMobile()"
                  text
                  inline
                  color="success"
                  :content="`${$t('misc.you')}`"
                >
                  {{
                    formatName(
                      item.timePairSummary.modifiedBy.firstName,
                      item.timePairSummary.modifiedBy.lastName
                    )
                  }}
                </v-badge>
              </template>
              <template v-slot:item.startDate="{ item }">
                <v-badge
                    :value="!isStartDateEqual(getCorrectionItemIndex(item),item.timePairSummary.clockIn.date)"
                    :left="isMobile()"
                    inline
                    text
                    dot
                    color="primary"
                >
                  <StartDateTableCell :item="item.timePairSummary"/>
                </v-badge>
              </template>
              <template v-slot:item.clockIn="{ item }">
                <v-badge
                  :value="!isClockInEqual(getCorrectionItemIndex(item),item.timePairSummary.clockIn.date)"
                  :left="isMobile()"
                  inline
                  text
                  dot
                  color="primary"
                >
                  <StartTimeTableCell :item="item.timePairSummary"/>
                </v-badge>
              </template>
              <template v-slot:item.clockOut="{ item }">
                <v-badge
                  :value="!isClockOutEqual(getCorrectionItemIndex(item), item.timePairSummary.clockOut.date)"
                  :left="isMobile()"
                  inline
                  text
                  dot
                  color="primary"
                >
                  <EndTimeTableCell :item="item.timePairSummary" :wrap="isMenuDrawerClosable()"/>
                </v-badge>
              </template>
              <template v-slot:item.duration="{ item }">
                {{ formatTimePairDuration(item.timePairSummary) }}
              </template>
              <template v-slot:item.status="{ item }">
                <ApprovalStatusTableCell :item="item.timePairSummary"/>
              </template>
              <template v-slot:item.reason="{ item }">
                {{ item.reason ? item.reason.description : ' - ' }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip top :disabled="isMobile()">
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      :class="actionIcon"
                      @click="viewTimePair(getCorrectionItemIndex(item), item)"
                    >
                      visibility
                    </v-icon>
                  </template>
                  <span>{{ $t('indicators.showDetails') }}</span>
                </v-tooltip>
                <v-icon
                  v-if="isMobile() && item.timePairSummary.segmentTimePairs.length > 0"
                  :class="actionIcon"
                  @click="viewSegmentTimePairs(item)"
                >
                  emoji_food_beverage
                </v-icon>
              </template>
              <template v-if="!isMobile()" v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pb-6 px-6 history-expanded-item">
                  <SegmentTimePairTable
                    :headers="segmentTimeDataTableHeaders"
                    :items="item.timePairSummary.segmentTimePairs"
                  >
                    <template v-slot:startDate="{ item: segmentTimePair }">
                      <v-badge
                          :value="!isSegmentStartDateEqual(getCorrectionItemIndex(item), segmentTimePair)"
                          :left="isMobile()"
                          inline
                          text
                          dot
                          color="primary"
                      >
                        <StartDateTableCell :item="segmentTimePair"/>
                      </v-badge>
                    </template>
                    <template v-slot:clockIn="{ item: segmentTimePair }">
                      <v-badge
                        :value="!isSegmentClockInEqual(getCorrectionItemIndex(item), segmentTimePair)"
                        :left="isMobile()"
                        inline
                        text
                        dot
                        color="primary"
                      >
                        <StartTimeTableCell :item="segmentTimePair"/>
                      </v-badge>
                    </template>
                    <template v-slot:clockOut="{ item: segmentTimePair }">
                      <v-badge
                        :value="!isSegmentClockOutEqual(getCorrectionItemIndex(item), segmentTimePair)"
                        :left="isMobile()"
                        inline
                        text
                        dot
                        color="primary"
                      >
                        <EndTimeTableCell :item="segmentTimePair"/>
                      </v-badge>
                    </template>
                  </SegmentTimePairTable>
                </td>
              </template>
            </v-data-table>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';

import constants from '@/constants';

import DateTimeUtil from '@/util/date-time-util';

import displayAdaptable from '@/mixins/displayAdaptable';
import getTimePairStatusIndicator from '@/mixins/timepairs/getTimePairStatusIndicator';
import hasActionIcon from '@/mixins/hasActionIcon';
import hasAppDialog from '@/mixins/hasAppDialog';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';

import StartDateTableCell from '@/components/timepairs/shared/StartDateTableCell.vue';
import StartTimeTableCell from '@/components/timepairs/shared/StartTimeTableCell.vue';
import EndTimeTableCell from '@/components/timepairs/shared/EndTimeTableCell.vue';
import TimePairStatusIndicator from '@/components/timepairs/TimePairStatusIndicator.vue';
import SegmentTimePairTable from '@/components/timepairs/SegmentTimePairTable.vue';
import SegmentTimePairTableDialog from '@/components/timepairs/SegmentTimePairTableDialog.vue';
import ApprovalStatusTableCell from '@/components/timepairs/shared/ApprovalStatusTableCell.vue';
import attachMobileBackButton from '@/mixins/attachMobileBackButton';
import filter from 'lodash/filter';

export default {
  name: 'TimePairHistoryDialog',
  components: {
    ApprovalStatusTableCell,
    EndTimeTableCell,
    StartTimeTableCell,
    StartDateTableCell,
    SegmentTimePairTableDialog,
    SegmentTimePairTable,
    TimePairStatusIndicator,
  },
  mixins: [
    attachMobileBackButton,
    displayAdaptable,
    getTimePairStatusIndicator,
    hasActionIcon,
    hasAppDialog,
    hasTimePairFeatures,
  ],

  props: {
    value: Boolean,
    timePair: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      historyTableHeaders: [
        {
          text: this.$t('tableHeaders.correctionDate'),
          value: constants.DataTableHeaderValues.CORRECTION_DATE,
        },
        {
          text: this.$t('tableHeaders.info'),
          value: constants.DataTableHeaderValues.INFO,
        },
        {
          text: this.$t('tableHeaders.modifiedBy'),
          value: constants.DataTableHeaderValues.MODIFIED_BY,
        },
        {
          text: this.$t('notifications.startDate'),
          value: constants.DataTableHeaderValues.START_DATE,
        },
        {
          text: this.$t('clock.clockIn'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
        },
        {
          text: this.$t('clock.clockOut'),
          value: constants.DataTableHeaderValues.CLOCK_OUT,
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
        },
        {
          text: this.$t('tableHeaders.status'),
          value: constants.DataTableHeaderValues.STATUS,
          align: 'center',
          inactive: () => !this.isTimePairApprovalFeatureActive,
        },
        {
          text: this.$t('titles.reason'),
          value: constants.DataTableHeaderValues.REASON,
        },
        {
          text: this.$t('tableHeaders.actions'),
          value: constants.DataTableHeaderValues.ACTIONS,
        },
      ],
      segmentTimeDataTableHeaders: [
        {
          text: this.$t('tableHeaders.info'),
          value: constants.DataTableHeaderValues.INFO,
        },
        {
          text: this.$t('tableHeaders.modifiedBy'),
          value: constants.DataTableHeaderValues.MODIFIED_BY,
        },
        {
          text: this.$t('notifications.startDate'),
          value: constants.DataTableHeaderValues.START_DATE,
        },
        {
          text: this.$t('clock.startTime'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
        },
        {
          text: this.$t('clock.endTime'),
          value: constants.DataTableHeaderValues.CLOCK_OUT,
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
        },
      ],
      loading: false,
      correctionData: {},
      correctionHistory: [],
      segmentTimePairViewDialog: false,
      viewingSegmentTimePairs: [],
      viewingSegmentTimePairsShift: {},
      viewingSegmentTimePairsEditable: false,
    };
  },

  computed: {
    ...mapState({
      employeeId: (state) => state.employee.id,
    }),

    displayedHistoryTableHeaders() {
      return filter(this.historyTableHeaders, (header) => (header.inactive ? !header.inactive() : true));
    },
  },

  watch: {
    timePair() {
      this.correctionData = {};
      this.correctionHistory = [];
      this.fetchTimePairHistory();
    },
  },

  methods: {
    ...mapActions(['loadTimePairCorrectionHistory']),

    closeOperation() {
      this.$emit('close');
    },

    getCorrectionItemIndex(item) {
      return findIndex(this.correctionHistory, item);
    },

    formatName(firstName, lastName) {
      return `${lastName} ${firstName.substring(0, 1)}.`;
    },

    convertDateToText(dateTime) {
      return DateTimeUtil.convertToHumanReadableDateTime(dateTime);
    },

    getRowIndicators(timePair) {
      const indicators = [];

      if (this.segmentTimePairsHaveChanged(timePair)) {
        indicators.push(this.getIndicatorObject(
          'segment_change',
          'emoji_food_beverage',
          constants.AppColors.PRIMARY,
          this.$t('indicators.breaksUpdated'),
        ));
      }

      if (timePair.isInactive) {
        indicators.push(this.getIndicatorObject(
          'delete',
          'delete',
          constants.AppColors.ERROR,
          timePair.deletedDate ? `${capitalize(timePair.pairType)} deleted on
          ${DateTimeUtil.convertToHumanReadableDate(timePair.deletedDate)}.`
            : this.$t('indicators.pendingDelete'),
        ));
      }

      return indicators;
    },

    segmentTimePairsHaveChanged(shiftTimePair) {
      const correctionIndex = findIndex(this.correctionHistory,
        { timePairSummary: { id: shiftTimePair.id } });
      if (correctionIndex === this.correctionHistory.length - 1) return false;

      return some(shiftTimePair.segmentTimePairs,
        (segmentPair) => segmentPair.previousVersionId == null)
        || some(shiftTimePair.segmentTimePairs, (segmentPair) => segmentPair.isCorrection)
        || some(shiftTimePair.segmentTimePairs, (segmentPair) => !!segmentPair.deletedDate);
    },

    formatTimePairDuration(timePair) {
      return DateTimeUtil.getFormattedDuration(timePair.roundedDuration);
    },

    isStartDateEqual(index, correctionDateTime) {
      if (index === this.correctionHistory.length - 1) return true;

      return DateTimeUtil.convertDateTimeToDate(correctionDateTime)
          === DateTimeUtil.convertDateTimeToDate(
            this.correctionHistory[index + 1].timePairSummary.clockIn.date,
          );
    },

    isClockInEqual(index, correctionDateTime) {
      if (index === this.correctionHistory.length - 1) return true;

      return correctionDateTime === this.correctionHistory[index + 1].timePairSummary.clockIn.date;
    },

    isSegmentStartDateEqual(index, segmentTimePair) {
      if (index === this.correctionHistory.length - 1) return true;

      const previousSegmentVersion = find(
        this.correctionHistory[index + 1].timePairSummary.segmentTimePairs,
        (segmentPair) => segmentPair.id === segmentTimePair.previousVersionId,
      );

      return previousSegmentVersion
        ? DateTimeUtil.convertDateTimeToDate(segmentTimePair.clockIn.date)
          === DateTimeUtil.convertDateTimeToDate(previousSegmentVersion.clockIn.date)
        : true;
    },

    isSegmentClockInEqual(index, segmentTimePair) {
      if (index === this.correctionHistory.length - 1) return true;

      const previousSegmentVersion = find(
        this.correctionHistory[index + 1].timePairSummary.segmentTimePairs,
        (segmentPair) => segmentPair.id === segmentTimePair.previousVersionId,
      );

      return previousSegmentVersion
        ? segmentTimePair.clockIn.date === previousSegmentVersion.clockIn.date
        : true;
    },

    isClockOutEqual(index, correctionDateTime) {
      if (index === this.correctionHistory.length - 1) return true;

      return correctionDateTime === this.correctionHistory[index + 1].timePairSummary.clockOut.date;
    },

    isSegmentClockOutEqual(index, segmentTimePair) {
      if (index === this.correctionHistory.length - 1) return true;

      const previousSegmentVersion = find(
        this.correctionHistory[index + 1].timePairSummary.segmentTimePairs,
        (segmentPair) => segmentPair.id === segmentTimePair.previousVersionId,
      );

      return previousSegmentVersion
        ? segmentTimePair.clockOut.date === previousSegmentVersion.clockOut.date
        : true;
    },

    viewTimePair(index, timePair) {
      const previousVersion = index === this.correctionHistory.length - 1 ? {}
        : this.correctionHistory[index + 1];

      this.$emit('view-time-pair', { timePair, previousVersion });
    },

    viewSegmentTimePairs(shiftTimePair, editable = false) {
      this.viewingSegmentTimePairs = shiftTimePair.timePairSummary
        ? shiftTimePair.timePairSummary.segmentTimePairs
        : shiftTimePair.segmentTimePairs;
      this.viewingSegmentTimePairsShift = shiftTimePair;
      this.viewingSegmentTimePairsEditable = editable;
      this.segmentTimePairViewDialog = true;
    },

    closeViewSegmentTimePairs() {
      this.viewingSegmentTimePairs = [];
      this.viewingSegmentTimePairsShift = {};
      this.viewingSegmentTimePairsEditable = false;
      this.segmentTimePairViewDialog = false;
    },

    fetchTimePairHistory() {
      if (isEmpty(this.timePair)) return;

      this.loading = true;
      this.loadTimePairCorrectionHistory(this.timePair.id)
      .then((response) => {
        this.correctionHistory = response.data;
      })
      .catch(() => {
        this.showAppErrorDialog({ message: this.$t('dialogs.error.noCorrectionHistory.message') });
      })
      .finally(() => {
        this.loading = false;
      });
    },
  },
};

</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
>>>.v-data-table__expanded.v-data-table__expanded__content {
  background-color: #f2f2f2;
  box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
  -webkit-box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
  -moz-box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
}

/*noinspection CssUnusedSymbol*/
>>>.v-data-table-header-mobile {
  display: none;
}
</style>
